import React, {ReactElement, SyntheticEvent, useLayoutEffect, useRef, useState} from 'react';
import Comment from '../entities/comment';
import "./CommentCard.less";
import util from "../lib/util";
import IconButton from "../ui/IconButton/IconButton";
import Icons from "../ui/IconButton/Icons";
import Auth from '../Auth/Auth';
import i18n from '../i18n/i18n';
import {getUserInitials} from "../Contacts/ContactFormat";
import {getCurrentUserId} from "../lib/picos/currentLogin";
import newHRTimestamp from "../lib/picos/timestamp";
import MultilineText from "../ui/MultilineText";
import {format} from "date-fns";
import TextField from "../ui/TextField";
import {useRootSelector} from "../hooks/useOutlawStore";
import useComponentCache from "../hooks/useComponentCache";
import {If} from 'react-conditional-components-renderer';

export type CommentCardProps = {
    comment: Comment,
    parentEntityType?: string, // The type of the parent
    onDelete: (comment: Comment) => void,
    onEdit: Function,
    isEditing?: boolean, // This refers to whether or not ANY of the comments are editable
    hasChanged?: boolean,
    visible?: boolean
}

type CommentCardState = {
    note: string,
    shadowNote: string,
    isEditing: boolean,
    hasChanged: boolean,
}

function CommentCard(props: CommentCardProps) {
    const comments = useRootSelector(state => state.comments);

    const {cacheSave, cacheReset, cacheValue} = useComponentCache<CommentCardState>(`comment-card-${props.comment.id}`);

    const [isEditing, setIsEditing] = useState<boolean>(cacheValue('isEditing', false));
    const [hasChanged, setHasChanged] = useState<boolean>(cacheValue('hasChanged', props.hasChanged));
    const [note, setNote] = useState<string>(cacheValue('note', props.comment.note));
    const [shadowNote, setShadowNote] = useState<string>(cacheValue('shadowNote', props.comment.note));

    const doResetRef = useRef<boolean>(false);
    
    useLayoutEffect(() => {
        return () => {
            // Read at cleanup time: Cancel/OK set the ref after this effect last ran.
            const doReset = doResetRef.current;
            if (doReset) cacheReset();
            else if (hasChanged) cacheSave({note, hasChanged, isEditing, shadowNote});
            // An unedited note is a copy of the comment and goes stale when the comment
            // changes elsewhere, so only the editing state is kept (OWA-526).
            else cacheSave({hasChanged, isEditing} as CommentCardState);
        }
    }, [cacheReset, cacheSave, hasChanged, isEditing, note, shadowNote]);

    const onBlur = (e: SyntheticEvent, _data: any, value: string) => {
        e.preventDefault();

        const _hasChanged = value !== props.comment.note;
        if (_hasChanged) {
            setHasChanged(hasChanged);
            setNote(value);
        }
    }

    const onChange = (e: SyntheticEvent, _data: any, value: string) => {
        e.preventDefault();

        setHasChanged(value !== props.comment.note);
        setShadowNote(value);
    }

    const onDelete = (e: SyntheticEvent) => {
        e.preventDefault();

        doResetRef.current = true;
        if (props.onDelete) props.onDelete(props.comment);
    };

    const onEditBegin = (e: SyntheticEvent) => {
        e.preventDefault();

        doResetRef.current = false;
        setIsEditing(true);
    };

    const onEditCancel = (e: SyntheticEvent) => {
        e.preventDefault();

        doResetRef.current = true;
        setNote(props.comment.note);
        setIsEditing(false);
    };

    /**
     *
     * @param {Event|string} e
     */
    const onEditEnd = (e: SyntheticEvent) => {
        const isEvent = typeof (e) !== 'string';
        if (isEvent) e.preventDefault();

        const comment = props.comment.clone();
        comment.note = isEvent ? shadowNote : e;
        comment.addedOn = newHRTimestamp();
        comment.addedBy = getCurrentUserId();

        props.onEdit(comment);

        doResetRef.current = true;
        setIsEditing(false);
        setNote(comment.note);
    };


    const {parentEntityType} = props;

    // Fall back to the passed-in comment when it isn't in the live slice —
    // e.g. a soft-deleted comment opened from Trash, or a not-yet-synced one.
    const comment = comments.get(props.comment.id) || props.comment;
    const createdDate = comment.createdOn || util.hrtToDate(comment.addedOn);
    const createdBy = getUserInitials(comment.createdBy || comment.addedBy, 3);
    const modifiedDate = util.hrtToDate(comment.addedOn);
    const modifiedBy = getUserInitials(comment.addedBy, 3);

    const by = i18n.c('by');
    const on = i18n.c('on');
    const created = i18n.c('created', true);
    const edited = i18n.c('edited', true);
    const createdString = format(createdDate, 'M/d/yy HH:mm');
    const modifiedString = format(modifiedDate, 'M/d/yy HH:mm');
    const same = createdString === modifiedString;

    let timestamp: ReactElement;
    if (same) timestamp = <div>{created} {by} {createdBy} {on} {createdString}</div>;
    else timestamp = <>
        <div>{created} {by} {createdBy} {on} {createdString},</div>
        <div>{edited} {by} {modifiedBy} {on} {modifiedString}</div>
    </>;

    // Check to see if this is the logged-in user.  If so, we add a star next to their name
    const isMe = ((getCurrentUserId() === comment.addedBy) || Auth.isAdmin(getCurrentUserId()) || Auth.isOwner())
                 ? i18n.c('me', true)
                 : '';

    // Check if this is a "related" task, meaning a task that belongs to another entity.
    // However, if we are in the Comments section of a task (not the containing entity), we do not want to display
    // it with special markings.
    let isRelated = parentEntityType !== comment.entityType &&
                    comment.sourceType !== comment.entityType &&
                    comment.sourceType === 'Task' ? 'RelatedTask' : '';

    let relatedIcon = null;
    switch (comment.sourceType) {
        case 'Task':
            if (parentEntityType !== 'task') {
                relatedIcon = Icons.TASKS;
            }
            break;
        case 'Lead':
            if (parentEntityType !== 'lead') {
                relatedIcon = Icons.LEAD;
                isRelated = 'RelatedLead';
            }
            break;
        case 'Matter':
            if (parentEntityType !== 'matter') {
                relatedIcon = Icons.CASE;
                isRelated = 'RelatedMatter';
            }
            break;
        case 'Contact':
            if (parentEntityType !== 'contact') {
                relatedIcon = Icons.CONTACT;
                isRelated = 'RelatedContact';
            }
            break;
        default:
            relatedIcon = Icons.QUESTION;
    }

    const {visible} = props;

    return (
        <div key={comment.id}
             className={`Comment ${isRelated} ${isMe}`}
             style={{visibility: visible ? 'visible' : 'hidden'}}
             onDoubleClick={onEditBegin}
        >
            {/* Author and date of comment */}
            <div className='Info'>

                {comment.sourceType && isRelated &&
					<IconButton type={relatedIcon} label={comment.sourceLabel} labelBold={true}/>}

                {/* Author */}
                <div key="author" className="Author">
                    {isMe && <IconButton type={Icons.STAR}
					                     color='black'
					/>}
                    {timestamp}
                </div>

                <div className={`NoteContainer ${isEditing ? 'editing' : ''}`}>
                    {isEditing ? <MultilineText onChange={onChange}
                                                onCancel={onEditCancel}
                                                onEnter={onEditEnd}
                                                onBlur={onBlur}
                                                resetOnEnter={true}
                                                key={comment.addedOn}
                                                id={comment.addedOn}
                                                value={note}
                                                isEditable={isEditing}
                                                stripHtml={true}
                    /> : <div className='Note' dangerouslySetInnerHTML={TextField.createMarkup(comment.note)}/>
                    }

                    {/* Edit mode buttons */}
                    {props.isEditing && !!isMe && (Auth.isAdmin(getCurrentUserId()) || !isRelated) &&
						<div className='u-row-nowrap'>
							<If test={isEditing}>
								<IconButton id={`CommentCard-EditCancel-${comment.id}`}
								            type={Icons.CLOSE}
								            useDelay={true}
								            onClick={visible ? onEditCancel : undefined}
								/>
								<IconButton id={`CommentCard-EditEnd-${comment.id}`}
								            type={Icons.CHECKMARK}
								            useDelay={true}
								            onClick={visible ? onEditEnd : undefined}
								            disabled={!hasChanged}
								/>
							</If>

                            {/* Display mode buttons */}
							<If test={!isEditing}>
								<IconButton id={`CommentCard-EditBegin-${comment.id}`}
								            type={Icons.EDIT}
								            useDelay={true}
								            title="Edit"
								            onClick={visible ? onEditBegin : undefined}
								/>
								<IconButton id={`CommentCard-Delete-${comment.id}`}
								            type={Icons.DELETE}
								            useDelay={true}
								            title="Delete"
								            onClick={visible ? onDelete : undefined}
								/>
							</If>
						</div>
                    }
                </div>
            </div>
        </div>
    );
}

export default CommentCard;
